import * as THREE from 'three';
import type { CharacterId, CharacterState, GameState, Team } from '../sim/types';
import { WORLD_HALF_X, WORLD_HALF_Z, TICK_RATE } from '../sim/sim';
import {
  castleRamps,
  castleSign,
  castleWalls,
  interiorTorches,
  keepBounds,
  roofedTowers,
  thronePosition,
} from '../sim/castle';
import type { Ramp, WallBox } from '../sim/castle';
import { BRIDGE, HOUSES, RIVER, obstacleList, plotOf } from '../sim/world';
import { squadLeader } from '../sim/squads';
import { heightAt } from './terrain';
import { CharacterRig } from './rig';

// Flat-color low-poly art direction: readable silhouettes, no textures.

const COLORS = {
  sky: 0x87a8c8,
  fog: 0x9ab2cc,
  groundLow: 0x55713f,
  groundHigh: 0x8a8f58,
  riverbed: 0x7a7052,
  water: 0x3f6f9e,
  stone: 0x84848c,
  stoneDark: 0x63636c,
  stoneLight: 0xa0a0a8,
  plank: 0x7a5940,
  towerRoof: 0x4f5b74,
  bannerFriendly: 0x3d5aa8,
  bannerEnemy: 0xa83d3d,
  pine: 0x3e5a35,
  oak: 0x4e6b33,
  trunk: 0x5a4632,
  rock: 0x7d7f83,
  houseWall: 0xb59a6d,
  houseRoof: 0x8a5a3a,
  arrow: 0x6b5334,
};

export interface PosSnapshot {
  x: number;
  z: number;
  elev: number;
  heading: number;
}

interface CharacterVisual {
  rig: CharacterRig;
  healthBar: THREE.Group | null;
  healthFill: THREE.Mesh | null;
  /** Team-colored ground disc — the strongest at-range faction signal. */
  disc: THREE.Mesh;
  farLod: boolean;
}

// LOD: rigs beyond FAR_DISTANCE stop casting shadows and only refresh their
// pose every 4th frame (position still updates every frame), with hysteresis.
const LOD_FAR_ENTER = 50;
const LOD_FAR_EXIT = 42;

export class Renderer {
  private renderer: THREE.WebGLRenderer;
  private scene = new THREE.Scene();
  private camera: THREE.PerspectiveCamera;
  private characters = new Map<CharacterId, CharacterVisual>();
  private buildingVisuals = new Map<string, { group: THREE.Group; complete: boolean }>();
  private arrowPool: THREE.Mesh[] = [];
  /** Interior torch lights per castle, enabled only when the subject is near. */
  private torchLights = new Map<Team, THREE.PointLight[]>();
  private lastRenderTime = performance.now();
  private frame = 0;

  cameraYaw = Math.PI / 2; // start looking east, toward the enemy side
  cameraPitch = 0.35;

  constructor(container: HTMLElement) {
    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    container.appendChild(this.renderer.domElement);

    this.camera = new THREE.PerspectiveCamera(60, window.innerWidth / window.innerHeight, 0.1, 900);

    this.scene.background = new THREE.Color(COLORS.sky);
    this.scene.fog = new THREE.Fog(COLORS.fog, 130, 560);

    this.buildLights();
    this.buildTerrain();
    this.buildWater();
    this.buildVegetation();
    this.buildVillage();
    this.buildCastles();

    window.addEventListener('resize', () => {
      this.camera.aspect = window.innerWidth / window.innerHeight;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(window.innerWidth, window.innerHeight);
    });
  }

  get canvas(): HTMLCanvasElement {
    return this.renderer.domElement;
  }

  private buildLights(): void {
    // strong sky fill so inward-facing courtyard walls never go black, and a
    // sun angled off the castle axis so adjacent box faces get distinct
    // values — in flat-color art, face contrast is all the form there is
    this.scene.add(new THREE.HemisphereLight(0xb8c8e0, 0x5e6a4a, 0.95));
    const sun = new THREE.DirectionalLight(0xfff1d6, 1.15);
    sun.position.set(-100, 150, 85);
    sun.castShadow = true;
    sun.shadow.normalBias = 0.8;
    sun.shadow.mapSize.set(2048, 2048);
    const s = 260;
    sun.shadow.camera.left = -s;
    sun.shadow.camera.right = s;
    sun.shadow.camera.top = s;
    sun.shadow.camera.bottom = -s;
    sun.shadow.camera.far = 500;
    this.scene.add(sun);
  }

  private buildTerrain(): void {
    const w = WORLD_HALF_X * 2 + 200;
    const d = WORLD_HALF_Z * 2 + 200;
    const segX = 150;
    const segZ = 110;
    const geo = new THREE.PlaneGeometry(w, d, segX, segZ);
    geo.rotateX(-Math.PI / 2);
    const pos = geo.attributes.position as THREE.BufferAttribute;
    const colors = new Float32Array(pos.count * 3);
    const low = new THREE.Color(COLORS.groundLow);
    const high = new THREE.Color(COLORS.groundHigh);
    const bed = new THREE.Color(COLORS.riverbed);
    const tmp = new THREE.Color();
    // hard-edged lighter meadow regions, painted into the vertex colors so
    // they conform to the terrain (no floating decals)
    const meadow = new THREE.Color(0x728a45);
    const meadows: [number, number, number][] = [
      [-55, 30, 13],
      [40, 55, 16],
      [90, -35, 12],
      [-15, -95, 15],
      [-100, -40, 11],
      [66, 14, 9],
    ];
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i);
      const z = pos.getZ(i);
      const h = heightAt(x, z);
      pos.setY(i, h);
      if (h < -0.6) {
        tmp.copy(bed);
      } else {
        // banded, not blended: flat-color style wants hard value steps
        const t = Math.max(0, Math.min(1, (h + 0.5) / 8));
        tmp.lerpColors(low, high, Math.round(t * 4) / 4);
        for (const [mx, mz, r] of meadows) {
          if ((x - mx) * (x - mx) + (z - mz) * (z - mz) < r * r) {
            tmp.copy(meadow);
            break;
          }
        }
      }
      colors[i * 3] = tmp.r;
      colors[i * 3 + 1] = tmp.g;
      colors[i * 3 + 2] = tmp.b;
    }
    geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    geo.computeVertexNormals();
    const ground = new THREE.Mesh(
      geo,
      new THREE.MeshLambertMaterial({ vertexColors: true, flatShading: true })
    );
    ground.receiveShadow = true;
    this.scene.add(ground);

    // the king's road: a dirt strip joining the two castle gates, with
    // fences and hay bales along it — the mid-map is a place, not a void
    const roadMat = new THREE.MeshLambertMaterial({ color: 0x77684c });
    const midRoad = new THREE.Mesh(new THREE.PlaneGeometry(230, 7), roadMat);
    midRoad.rotation.x = -Math.PI / 2;
    midRoad.position.set(0, 0.055, 0);
    this.scene.add(midRoad);
    const fenceMat = new THREE.MeshLambertMaterial({ color: 0x6b5334 });
    const fenceAt = (fx: number, fz: number, alongX: boolean, len: number) => {
      const posts = Math.max(2, Math.round(len / 2.2));
      for (let p = 0; p < posts; p++) {
        const t = (p / (posts - 1) - 0.5) * len;
        const px = fx + (alongX ? t : 0);
        const pz = fz + (alongX ? 0 : t);
        const post = new THREE.Mesh(new THREE.BoxGeometry(0.14, 1.0, 0.14), fenceMat);
        post.position.set(px, heightAt(px, pz) + 0.5, pz);
        post.castShadow = true;
        this.scene.add(post);
      }
      const rail = new THREE.Mesh(
        new THREE.BoxGeometry(alongX ? len : 0.09, 0.09, alongX ? 0.09 : len),
        fenceMat
      );
      rail.position.set(fx, heightAt(fx, fz) + 0.82, fz);
      this.scene.add(rail);
    };
    fenceAt(-62, 7.5, true, 16);
    fenceAt(48, -7.5, true, 13);
    fenceAt(10, 8, true, 10);
    const hayMat = new THREE.MeshLambertMaterial({ color: 0xc8b060 });
    for (const [hx, hz] of [
      [-60, 10.5],
      [-57, 10],
      [50, -10.5],
      [12, 10.6],
    ]) {
      const hay = new THREE.Mesh(new THREE.CylinderGeometry(0.75, 0.75, 0.95, 9), hayMat);
      hay.rotation.z = Math.PI / 2;
      hay.position.set(hx, heightAt(hx, hz) + 0.75, hz);
      hay.castShadow = true;
      this.scene.add(hay);
    }
  }

  private buildWater(): void {
    const water = new THREE.Mesh(
      new THREE.PlaneGeometry(RIVER.maxX - RIVER.minX + 8, 150 - RIVER.minZ + 10),
      new THREE.MeshLambertMaterial({ color: COLORS.water, transparent: true, opacity: 0.85 })
    );
    water.rotation.x = -Math.PI / 2;
    water.position.set(
      (RIVER.minX + RIVER.maxX) / 2,
      -1.0,
      (RIVER.minZ + 150) / 2
    );
    this.scene.add(water);

    // bridge deck + rails across the gap
    const deckMat = new THREE.MeshLambertMaterial({ color: 0x8a6f4d });
    const deck = new THREE.Mesh(
      new THREE.BoxGeometry(RIVER.maxX - RIVER.minX + 10, 0.4, BRIDGE.maxZ - BRIDGE.minZ),
      deckMat
    );
    deck.position.set((RIVER.minX + RIVER.maxX) / 2, 0.1, (BRIDGE.minZ + BRIDGE.maxZ) / 2);
    deck.castShadow = deck.receiveShadow = true;
    this.scene.add(deck);
    for (const side of [-1, 1]) {
      const rail = new THREE.Mesh(
        new THREE.BoxGeometry(RIVER.maxX - RIVER.minX + 10, 0.8, 0.3),
        deckMat
      );
      rail.position.set(
        (RIVER.minX + RIVER.maxX) / 2,
        0.7,
        (BRIDGE.minZ + BRIDGE.maxZ) / 2 + side * ((BRIDGE.maxZ - BRIDGE.minZ) / 2)
      );
      rail.castShadow = true;
      this.scene.add(rail);
    }
  }

  /** Deterministic pseudo-random from an index. */
  private static rand(i: number, salt: number): number {
    const v = Math.sin(i * 127.1 + salt * 311.7) * 43758.5453;
    return v - Math.floor(v);
  }

  private buildVegetation(): void {
    const trunkGeo = new THREE.CylinderGeometry(0.3, 0.45, 2.6, 6);
    const pineGeo = new THREE.ConeGeometry(2.0, 5.2, 7);
    const oakGeo = new THREE.SphereGeometry(2.3, 7, 6);
    const rockGeo = new THREE.DodecahedronGeometry(1, 0);
    const trunkMat = new THREE.MeshLambertMaterial({ color: COLORS.trunk });
    const pineMat = new THREE.MeshLambertMaterial({ color: COLORS.pine });
    const oakMat = new THREE.MeshLambertMaterial({ color: COLORS.oak });
    const rockMat = new THREE.MeshLambertMaterial({ color: COLORS.rock });

    // clustered forests + scattered copses — props are the texture in this
    // style, so density matters
    const clusters = [
      { x: -85, z: 82, r: 48, n: 130, pine: 0.75 }, // NW forest on the hill
      { x: 85, z: -66, r: 40, n: 85, pine: 0.9 }, // SE ridge pines
      { x: -95, z: -95, r: 34, n: 50, pine: 0.4 },
      { x: 60, z: 95, r: 40, n: 55, pine: 0.5 },
      { x: 118, z: 55, r: 26, n: 26, pine: 0.6 },
      { x: -120, z: -45, r: 24, n: 22, pine: 0.7 },
      { x: 0, z: 0, r: 200, n: 95, pine: 0.5 }, // sparse everywhere
    ];
    let planted = 0;
    clusters.forEach((cl, ci) => {
      for (let i = 0; i < cl.n; i++) {
        const a = Renderer.rand(i + ci * 977, 1) * Math.PI * 2;
        const r = Math.sqrt(Renderer.rand(i + ci * 977, 2)) * cl.r;
        const x = cl.x + Math.cos(a) * r;
        const z = cl.z + Math.sin(a) * r;
        // keep out of gameplay-critical zones
        if (Math.abs(x) > 144 || Math.abs(z) > 145) continue;
        if (x > RIVER.minX - 5 && x < RIVER.maxX + 5 && z > RIVER.minZ - 4) continue;
        if (Math.hypot(x - 25, z + 50) < 26) continue; // village
        if (Math.abs(z) < 22 && Math.abs(x) < 140 && Renderer.rand(i + ci * 977, 3) < 0.72)
          continue; // thin out the battle corridor
        const h = heightAt(x, z);
        const scale = 0.7 + Renderer.rand(i + ci * 977, 4) * 0.8;
        const trunk = new THREE.Mesh(trunkGeo, trunkMat);
        trunk.position.set(x, h + 1.3 * scale, z);
        trunk.scale.setScalar(scale);
        const isPine = Renderer.rand(i + ci * 977, 5) < cl.pine;
        const crown = new THREE.Mesh(isPine ? pineGeo : oakGeo, isPine ? pineMat : oakMat);
        crown.position.set(x, h + (isPine ? 4.6 : 4.2) * scale, z);
        crown.scale.setScalar(scale);
        trunk.castShadow = crown.castShadow = true;
        this.scene.add(trunk, crown);
        planted++;
      }
    });
    void planted;

    // bushes: squashed low domes scattered over the open ground
    const bushGeo = new THREE.DodecahedronGeometry(1, 0);
    const bushMat = new THREE.MeshLambertMaterial({ color: 0x4a6b35 });
    for (let i = 0; i < 90; i++) {
      const x = (Renderer.rand(i, 21) - 0.5) * 2 * (WORLD_HALF_X - 40);
      const z = (Renderer.rand(i, 22) - 0.5) * 2 * (WORLD_HALF_Z - 20);
      if (Math.abs(x) > 144) continue;
      if (x > RIVER.minX - 4 && x < RIVER.maxX + 4 && z > RIVER.minZ) continue;
      if (Math.hypot(x - 25, z + 50) < 24) continue;
      if (Math.abs(z) < 8 && Math.abs(x) < 120) continue; // keep the road clear
      const s = 0.5 + Renderer.rand(i, 23) * 0.7;
      const bush = new THREE.Mesh(bushGeo, bushMat);
      bush.position.set(x, heightAt(x, z) + s * 0.35, z);
      bush.scale.set(s, s * 0.55, s);
      bush.rotation.y = Renderer.rand(i, 24) * Math.PI;
      bush.castShadow = true;
      this.scene.add(bush);
    }

    // boulders
    for (let i = 0; i < 55; i++) {
      const x = (Renderer.rand(i, 6) - 0.5) * 2 * (WORLD_HALF_X - 30);
      const z = (Renderer.rand(i, 7) - 0.5) * 2 * (WORLD_HALF_Z - 20);
      if (Math.abs(x) > 144) continue;
      if (x > RIVER.minX - 4 && x < RIVER.maxX + 4 && z > RIVER.minZ) continue;
      if (Math.hypot(x - 25, z + 50) < 24) continue;
      const s = 0.4 + Renderer.rand(i, 8) * 1.3;
      const rock = new THREE.Mesh(rockGeo, rockMat);
      rock.position.set(x, heightAt(x, z) + s * 0.3, z);
      rock.scale.set(s, s * (0.6 + Renderer.rand(i, 9) * 0.5), s);
      rock.rotation.y = Renderer.rand(i, 10) * Math.PI;
      rock.castShadow = true;
      this.scene.add(rock);
    }
  }

  private buildVillage(): void {
    const wallMat = new THREE.MeshLambertMaterial({ color: COLORS.houseWall });
    const roofMat = new THREE.MeshLambertMaterial({ color: COLORS.houseRoof });
    for (const house of HOUSES) {
      const b = house.box;
      const w = b.maxX - b.minX;
      const d = b.maxZ - b.minZ;
      const cx = (b.minX + b.maxX) / 2;
      const cz = (b.minZ + b.maxZ) / 2;
      const y = heightAt(cx, cz);
      const body = new THREE.Mesh(new THREE.BoxGeometry(w, b.height, d), wallMat);
      body.position.set(cx, y + b.height / 2, cz);
      body.castShadow = body.receiveShadow = true;
      // pyramid-ish roof: a 4-sided cone squashed along the ridge
      const roof = new THREE.Mesh(new THREE.ConeGeometry(0.72, b.height * 0.55, 4), roofMat);
      roof.scale.set(house.alongX ? w : d, 1, house.alongX ? d : w);
      roof.rotation.y = Math.PI / 4;
      roof.position.set(cx, y + b.height + b.height * 0.27, cz);
      roof.castShadow = true;
      this.scene.add(body, roof);
    }
  }

  /** One axis-aligned stone box (walls, parapet bands, tower bodies). */
  private stoneBox(w: WallBox, tint: number): THREE.Mesh {
    const minY = w.minY ?? 0;
    const mesh = new THREE.Mesh(
      new THREE.BoxGeometry(w.maxX - w.minX, w.height - minY, w.maxZ - w.minZ),
      new THREE.MeshLambertMaterial({
        color: new THREE.Color(COLORS.stone).multiplyScalar(tint),
      })
    );
    mesh.position.set(
      (w.minX + w.maxX) / 2,
      minY + (w.height - minY) / 2,
      (w.minZ + w.maxZ) / 2
    );
    mesh.castShadow = mesh.receiveShadow = true;
    return mesh;
  }

  /** Merlons along a parapet band's long axis (an InstancedMesh per band).
   * Battlements read through rhythm: fixed 0.8m merlons on a strict 1.45m
   * beat, taller than wide, one value lighter than the wall body. */
  private crenellate(w: WallBox, bandTop: number): THREE.InstancedMesh {
    const alongX = w.maxX - w.minX >= w.maxZ - w.minZ;
    const span = alongX ? w.maxX - w.minX : w.maxZ - w.minZ;
    const count = Math.max(1, Math.round(span / 1.45));
    const merlonH = w.height - bandTop;
    const geo = alongX
      ? new THREE.BoxGeometry(0.8, merlonH, w.maxZ - w.minZ)
      : new THREE.BoxGeometry(w.maxX - w.minX, merlonH, 0.8);
    const mesh = new THREE.InstancedMesh(
      geo,
      new THREE.MeshLambertMaterial({ color: COLORS.stoneLight }),
      count
    );
    const m = new THREE.Matrix4();
    for (let i = 0; i < count; i++) {
      const t = ((i + 0.5) / count) * span;
      const x = alongX ? w.minX + t : (w.minX + w.maxX) / 2;
      const z = alongX ? (w.minZ + w.maxZ) / 2 : w.minZ + t;
      m.setPosition(x, bandTop + merlonH / 2, z);
      mesh.setMatrixAt(i, m);
    }
    mesh.castShadow = true;
    return mesh;
  }

  /** Chunky stone steps for a stair ramp. */
  private buildStairs(r: Ramp): void {
    const mat = new THREE.MeshLambertMaterial({ color: COLORS.stoneDark });
    const rise = Math.abs(r.h1 - r.h0);
    const steps = Math.max(4, Math.round(rise / 0.55));
    const alongX = r.axis === 'x';
    const run = alongX ? r.maxX - r.minX : r.maxZ - r.minZ;
    for (let i = 0; i < steps; i++) {
      const t0 = i / steps;
      const t1 = (i + 1) / steps;
      const h = r.h0 + (r.h1 - r.h0) * (t0 + t1) * 0.5;
      if (h < 0.15) continue;
      const stepLen = run / steps + 0.05;
      const geo = alongX
        ? new THREE.BoxGeometry(stepLen, h, r.maxZ - r.minZ)
        : new THREE.BoxGeometry(r.maxX - r.minX, h, stepLen);
      const mesh = new THREE.Mesh(geo, mat);
      const mid = t0 * run + run / steps / 2;
      mesh.position.set(
        alongX ? r.minX + mid : (r.minX + r.maxX) / 2,
        h / 2,
        alongX ? (r.minZ + r.maxZ) / 2 : r.minZ + mid
      );
      mesh.castShadow = mesh.receiveShadow = true;
      this.scene.add(mesh);
    }
  }

  private buildFlag(x: number, y: number, z: number, team: Team, scale = 1): void {
    const pole = new THREE.Mesh(
      new THREE.CylinderGeometry(0.08 * scale, 0.08 * scale, 3.4 * scale, 6),
      new THREE.MeshLambertMaterial({ color: 0x4a3a28 })
    );
    pole.position.set(x, y + 1.7 * scale, z);
    const flag = new THREE.Mesh(
      new THREE.PlaneGeometry(1.9 * scale, 1.0 * scale),
      new THREE.MeshLambertMaterial({
        color: team === 'friendly' ? COLORS.bannerFriendly : COLORS.bannerEnemy,
        side: THREE.DoubleSide,
      })
    );
    // fly toward the field so both castles' colors read from the battlefield
    flag.position.set(x - castleSign(team) * 0.98 * scale, y + 2.85 * scale, z);
    flag.rotation.y = Math.PI / 2;
    this.scene.add(pole, flag);
  }

  private buildCastles(): void {
    for (const team of ['friendly', 'enemy'] as Team[]) {
      const s = castleSign(team);
      const walls = castleWalls(team);
      const plankMat = new THREE.MeshLambertMaterial({ color: COLORS.plank });
      const plinthMat = new THREE.MeshLambertMaterial({ color: COLORS.stoneDark });
      walls.forEach((w, i) => {
        const tint = 0.94 + Renderer.rand(i, team === 'friendly' ? 41 : 42) * 0.1;
        if (w.crenellated) {
          const minY = w.minY ?? 0;
          const bandTop = minY + (w.height - minY) * 0.35;
          this.scene.add(this.stoneBox({ ...w, height: bandTop }, tint));
          this.scene.add(this.crenellate(w, bandTop));
          return;
        }
        const mesh = this.stoneBox(w, tint);
        if (w.tag === 'roof') {
          (mesh.material as THREE.MeshLambertMaterial).color.set(COLORS.stoneDark);
        }
        this.scene.add(mesh);
        // walkable wall tops get a plank walkway strip; tall ground-based
        // walls get a darker plinth course — three stone values, not one
        if (w.walkable && !w.tag && w.height <= 7.5 && (w.minY ?? 0) < 6) {
          const plank = new THREE.Mesh(
            new THREE.BoxGeometry(w.maxX - w.minX - 0.2, 0.09, w.maxZ - w.minZ - 0.2),
            plankMat
          );
          plank.position.set(
            (w.minX + w.maxX) / 2,
            w.height + 0.045,
            (w.minZ + w.maxZ) / 2
          );
          this.scene.add(plank);
        }
        if ((w.minY ?? 0) === 0 && w.height >= 5) {
          const plinth = new THREE.Mesh(
            new THREE.BoxGeometry(w.maxX - w.minX + 0.14, 0.55, w.maxZ - w.minZ + 0.14),
            plinthMat
          );
          plinth.position.set((w.minX + w.maxX) / 2, 0.275, (w.minZ + w.maxZ) / 2);
          plinth.receiveShadow = true;
          this.scene.add(plinth);
        }
      });

      for (const r of castleRamps(team)) this.buildStairs(r);

      // conical roofs on the rear corner towers
      for (const t of roofedTowers(team)) {
        const cone = new THREE.Mesh(
          new THREE.ConeGeometry(t.radius + 0.5, 7.5, 8),
          new THREE.MeshLambertMaterial({ color: COLORS.towerRoof })
        );
        cone.position.set(t.x, t.base + 3.75, t.z);
        cone.castShadow = true;
        this.scene.add(cone);
      }

      // arrow slits down the outward faces: uniform, gridded, recessed dark
      // (not pure black) so they read as openings rather than decals
      const slitMat = new THREE.MeshLambertMaterial({ color: 0x26262b });
      const slit = (x: number, y: number, z: number) => {
        const m = new THREE.Mesh(new THREE.BoxGeometry(0.2, 1.05, 0.24), slitMat);
        m.position.set(x, y, z);
        this.scene.add(m);
      };
      for (const z of [-48, -38, -28, -18, 18, 28, 38, 48]) slit(s * 161.92, 4.4, z);
      for (const z of [-8.7, -6.3, 6.3, 8.7]) {
        slit(s * 159.92, 4.2, z);
        slit(s * 159.92, 6.8, z);
      }

      // the gate itself: oak doors swung open and a half-raised portcullis —
      // an entrance, not a hole in the mesh
      const oakMat = new THREE.MeshLambertMaterial({ color: 0x5a3c22 });
      for (const side of [1, -1]) {
        const door = new THREE.Mesh(new THREE.BoxGeometry(0.25, 4.3, 3.6), oakMat);
        const hinge = new THREE.Group();
        hinge.position.set(s * 164.2, 2.15, side * 3.9);
        hinge.rotation.y = side * s * 0.62;
        door.position.z = -side * 1.8;
        door.castShadow = true;
        hinge.add(door);
        this.scene.add(hinge);
      }
      const ironMat = new THREE.MeshLambertMaterial({ color: 0x3a3a3f });
      for (let bar = 0; bar < 7; bar++) {
        const v = new THREE.Mesh(new THREE.BoxGeometry(0.13, 3.1, 0.13), ironMat);
        v.position.set(s * 162.7, 5.0, -3.45 + bar * 1.15);
        this.scene.add(v);
      }
      for (const hy of [4.1, 5.9]) {
        const h = new THREE.Mesh(new THREE.BoxGeometry(0.13, 0.13, 7.2), ironMat);
        h.position.set(s * 162.7, hy, 0);
        this.scene.add(h);
      }

      // packed-earth road out the gate, and a worn courtyard
      const dirtMat = new THREE.MeshLambertMaterial({ color: 0x77684c });
      const road = new THREE.Mesh(new THREE.PlaneGeometry(52, 9), dirtMat);
      road.rotation.x = -Math.PI / 2;
      road.position.set(s * 139, 0.05, 0);
      road.receiveShadow = true;
      const yard = new THREE.Mesh(
        new THREE.PlaneGeometry(31, 70),
        new THREE.MeshLambertMaterial({ color: 0x6e6549 })
      );
      yard.rotation.x = -Math.PI / 2;
      yard.position.set(s * 180.5, 0.05, 0);
      yard.receiveShadow = true;
      this.scene.add(road, yard);

      // a well in the courtyard: stone ring, corner posts, snug roof
      const wellBase = new THREE.Mesh(
        new THREE.CylinderGeometry(1.05, 1.15, 1.0, 8),
        new THREE.MeshLambertMaterial({ color: COLORS.stoneDark })
      );
      wellBase.position.set(s * 178, 0.5, 24);
      wellBase.castShadow = true;
      this.scene.add(wellBase);
      const postMat = new THREE.MeshLambertMaterial({ color: 0x4a3626 });
      for (const [px, pz] of [
        [0.8, 0.8],
        [-0.8, -0.8],
        [0.8, -0.8],
        [-0.8, 0.8],
      ]) {
        const post = new THREE.Mesh(new THREE.BoxGeometry(0.12, 2.2, 0.12), postMat);
        post.position.set(s * 178 + px, 1.1, 24 + pz);
        this.scene.add(post);
      }
      const wellRoof = new THREE.Mesh(
        new THREE.ConeGeometry(1.05, 1.0, 4),
        new THREE.MeshLambertMaterial({ color: COLORS.houseRoof })
      );
      wellRoof.rotation.y = Math.PI / 4;
      wellRoof.position.set(s * 178, 2.7, 24);
      wellRoof.castShadow = true;
      this.scene.add(wellRoof);

      // team colors: keep-roof standard + banners on the gatehouse towers
      const kb = keepBounds(team);
      this.buildFlag((kb.minX + kb.maxX) / 2, kb.height, (kb.minZ + kb.maxZ) / 2, team, 1.5);
      for (const z of [7.5, -7.5]) {
        const banner = new THREE.Mesh(
          new THREE.PlaneGeometry(1.7, 4.2),
          new THREE.MeshLambertMaterial({
            color: team === 'friendly' ? COLORS.bannerFriendly : COLORS.bannerEnemy,
            side: THREE.DoubleSide,
          })
        );
        banner.position.set(s * 159.8, 6.4, z);
        banner.rotation.y = s * (Math.PI / 2);
        this.scene.add(banner);
        this.buildFlag(s * 164, 10, z + (z > 0 ? 0 : 0), team, 0.9);
      }

      this.buildInterior(team);
    }
  }

  /** Keep interiors: stone floor, carpet, columns, banners, torches with
   * point lights, throne, and simple furniture per room. */
  private buildInterior(team: Team): void {
    const s = castleSign(team);
    const wood = new THREE.MeshLambertMaterial({ color: 0x6b4e30 });
    const woodDark = new THREE.MeshLambertMaterial({ color: 0x53381f });
    const teamColor = team === 'friendly' ? COLORS.bannerFriendly : COLORS.bannerEnemy;

    // stone floor across the keep, red carpet from the door to the throne
    const floor = new THREE.Mesh(
      new THREE.PlaneGeometry(35, 61),
      new THREE.MeshLambertMaterial({ color: 0x5c5c64 })
    );
    floor.rotation.x = -Math.PI / 2;
    floor.position.set(s * 215, 0.07, 0);
    floor.receiveShadow = true;
    const carpet = new THREE.Mesh(
      new THREE.PlaneGeometry(28, 3.2),
      new THREE.MeshLambertMaterial({ color: 0x7d2828 })
    );
    carpet.rotation.x = -Math.PI / 2;
    carpet.position.set(s * 212, 0.1, 0);
    this.scene.add(floor, carpet);

    // hall columns (visual cylinders over the sim's square collision posts),
    // with capitals so they don't just vanish into the dark ceiling
    const colMat = new THREE.MeshLambertMaterial({ color: COLORS.stone });
    const capMat = new THREE.MeshLambertMaterial({ color: COLORS.stoneLight });
    for (const cx of [208, 214, 220, 226]) {
      for (const cz of [8, -8]) {
        const col = new THREE.Mesh(new THREE.CylinderGeometry(0.48, 0.6, 8.6, 8), colMat);
        col.position.set(s * cx, 4.3, cz);
        col.castShadow = true;
        const capital = new THREE.Mesh(new THREE.BoxGeometry(1.3, 0.35, 1.3), capMat);
        capital.position.set(s * cx, 5.55, cz);
        const base = new THREE.Mesh(new THREE.BoxGeometry(1.4, 0.35, 1.4), capMat);
        base.position.set(s * cx, 0.18, cz);
        this.scene.add(col, capital, base);
      }
    }

    // rafter beams cap the hall visually (the roof slab is 9m up; the beams
    // bring the perceived ceiling down to something human)
    const rafterMat = new THREE.MeshLambertMaterial({ color: 0x4a3626 });
    for (const cx of [208, 214, 220, 226]) {
      const beam = new THREE.Mesh(new THREE.BoxGeometry(0.4, 0.45, 27), rafterMat);
      beam.position.set(s * cx, 5.95, 0);
      this.scene.add(beam);
    }
    const ridge = new THREE.Mesh(new THREE.BoxGeometry(29, 0.35, 0.4), rafterMat);
    ridge.position.set(s * 218, 6.2, 0);
    this.scene.add(ridge);

    // wall banners down the hall and flanking the throne
    for (const bx of [210, 216, 222]) {
      for (const bz of [13.35, -13.35]) {
        const banner = new THREE.Mesh(
          new THREE.PlaneGeometry(1.4, 3.4),
          new THREE.MeshLambertMaterial({ color: teamColor, side: THREE.DoubleSide })
        );
        banner.position.set(s * bx, 2.9, bz + (bz > 0 ? -0.1 : 0.1));
        this.scene.add(banner);
      }
    }
    for (const bz of [3.5, -3.5]) {
      const banner = new THREE.Mesh(
        new THREE.PlaneGeometry(1.6, 4.6),
        new THREE.MeshLambertMaterial({ color: teamColor, side: THREE.DoubleSide })
      );
      banner.position.set(s * 232.3, 3.6, bz);
      banner.rotation.y = s * (Math.PI / 2);
      this.scene.add(banner);
    }

    // torches: sconce + flame everywhere, a handful of real lights per room
    const flameMat = new THREE.MeshBasicMaterial({ color: 0xffa540 });
    for (const t of interiorTorches(team)) {
      const bracket = new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.55, 0.14), woodDark);
      bracket.position.set(t.x, t.y - 0.3, t.z);
      const flame = new THREE.Mesh(new THREE.ConeGeometry(0.16, 0.45, 6), flameMat);
      flame.position.set(t.x, t.y + 0.12, t.z);
      this.scene.add(bracket, flame);
    }
    const lights: THREE.PointLight[] = [];
    const lightSpots = [
      { x: 212, y: 4.5, z: 0, i: 30, d: 30 }, // hall west
      { x: 224, y: 4.5, z: 0, i: 26, d: 26 }, // hall east
      { x: 228.5, y: 4.0, z: 0, i: 16, d: 11 }, // warm pool on the throne
      { x: 200, y: 3.6, z: 0, i: 20, d: 24 }, // corridor
      { x: 226, y: 3.6, z: 22, i: 22, d: 24 }, // north chamber
      { x: 226, y: 3.6, z: -22, i: 22, d: 24 }, // south chamber
      { x: 200, y: 2.6, z: -29, i: 14, d: 16 }, // cellar
    ];
    for (const l of lightSpots) {
      const light = new THREE.PointLight(0xffb066, l.i, l.d, 2);
      light.position.set(s * l.x, l.y, l.z);
      light.visible = false;
      this.scene.add(light);
      lights.push(light);
    }
    this.torchLights.set(team, lights);

    // the throne: a real silhouette — two-step dais, high gold back with
    // finials, seat and arms — worthy of being the composition's destination
    const throne = thronePosition(team);
    const gold = new THREE.MeshLambertMaterial({ color: 0xc9962e });
    const dais = new THREE.Mesh(
      new THREE.BoxGeometry(5.4, 0.25, 4.6),
      new THREE.MeshLambertMaterial({ color: 0x6a6a74 })
    );
    dais.position.set(throne.x, 0.125, throne.z);
    const dais2 = new THREE.Mesh(
      new THREE.BoxGeometry(3.8, 0.25, 3.4),
      new THREE.MeshLambertMaterial({ color: 0x7d7d88 })
    );
    dais2.position.set(throne.x + s * 0.4, 0.37, throne.z);
    dais.receiveShadow = dais2.receiveShadow = true;
    const seat = new THREE.Mesh(new THREE.BoxGeometry(0.9, 0.65, 1.0), woodDark);
    seat.position.set(throne.x + s * 1.1, 0.85, throne.z);
    const back = new THREE.Mesh(new THREE.BoxGeometry(0.18, 2.6, 1.15), gold);
    back.position.set(throne.x + s * 1.6, 1.85, throne.z);
    seat.castShadow = back.castShadow = true;
    this.scene.add(dais, dais2, seat, back);
    for (const az of [-0.55, 0.55]) {
      const arm = new THREE.Mesh(new THREE.BoxGeometry(1.0, 0.14, 0.12), gold);
      arm.position.set(throne.x + s * 1.1, 1.35, throne.z + az);
      this.scene.add(arm);
      const finial = new THREE.Mesh(new THREE.SphereGeometry(0.12, 7, 6), gold);
      finial.position.set(throne.x + s * 1.6, 3.25, throne.z + az * 0.95);
      this.scene.add(finial);
    }

    // furniture: hall table, chamber beds + chests, cellar barrels
    const table = new THREE.Mesh(new THREE.BoxGeometry(6, 0.25, 1.5), wood);
    table.position.set(s * 212, 1.0, 11);
    table.castShadow = true;
    for (const lx of [-2.6, 2.6]) {
      const leg = new THREE.Mesh(new THREE.BoxGeometry(0.25, 1.0, 1.3), woodDark);
      leg.position.set(s * 212 + lx, 0.5, 11);
      this.scene.add(leg);
    }
    const bench = new THREE.Mesh(new THREE.BoxGeometry(5.4, 0.5, 0.5), woodDark);
    bench.position.set(s * 212, 0.25, 9.6);
    this.scene.add(table, bench);

    for (const bz of [26, -26]) {
      const bed = new THREE.Mesh(new THREE.BoxGeometry(1.7, 0.55, 2.6), wood);
      bed.position.set(s * 230.5, 0.35, bz + (bz > 0 ? 2 : -2));
      const blanket = new THREE.Mesh(
        new THREE.BoxGeometry(1.72, 0.2, 1.7),
        new THREE.MeshLambertMaterial({ color: teamColor })
      );
      blanket.position.set(s * 230.5, 0.62, bz + (bz > 0 ? 1.6 : -1.6));
      const chest = new THREE.Mesh(new THREE.BoxGeometry(1.1, 0.75, 0.7), woodDark);
      chest.position.set(s * 226, 0.4, bz + (bz > 0 ? 3.6 : -3.6));
      bed.castShadow = chest.castShadow = true;
      this.scene.add(bed, blanket, chest);
    }

    const barrelMat = new THREE.MeshLambertMaterial({ color: 0x7a5a36 });
    const barrels: [number, number][] = [
      [199, -30.5],
      [201.3, -30],
      [199.2, -27.3],
    ];
    for (const [bx, bz] of barrels) {
      const barrel = new THREE.Mesh(new THREE.CylinderGeometry(0.55, 0.55, 1.15, 8), barrelMat);
      barrel.position.set(s * bx, 0.58, bz);
      barrel.castShadow = true;
      this.scene.add(barrel);
    }
  }

  /** A construction site: corner posts and ring beams in raw timber. */
  private buildScaffold(group: THREE.Group): void {
    const wood = new THREE.MeshLambertMaterial({ color: 0x8a6f4d });
    for (const sx of [-2.6, 2.6]) {
      for (const sz of [-2.6, 2.6]) {
        const post = new THREE.Mesh(new THREE.BoxGeometry(0.22, 3.2, 0.22), wood);
        post.position.set(sx, 1.6, sz);
        post.castShadow = true;
        group.add(post);
      }
    }
    for (const [w, d, x, z] of [
      [5.4, 0.18, 0, -2.6],
      [5.4, 0.18, 0, 2.6],
      [0.18, 5.4, -2.6, 0],
      [0.18, 5.4, 2.6, 0],
    ]) {
      const beam = new THREE.Mesh(new THREE.BoxGeometry(w, 0.18, d), wood);
      beam.position.set(x, 2.9, z);
      group.add(beam);
    }
    const pile = new THREE.Mesh(
      new THREE.BoxGeometry(1.6, 0.6, 1.1),
      new THREE.MeshLambertMaterial({ color: 0x9a8a72 })
    );
    pile.position.set(0, 0.3, 0);
    group.add(pile);
  }

  /** Bailey building shell: four walls with a courtyard-facing door and a
   * pyramid roof (matches the sim's collision walls). */
  private baileyShell(
    group: THREE.Group,
    plotZ: number,
    wallColor: number,
    roofColor: number,
    wallH = 3.5
  ): void {
    const wallMat = new THREE.MeshLambertMaterial({ color: wallColor });
    const doorSide = plotZ > 0 ? -1 : 1; // door faces the courtyard (z -> 0)
    const boxes: [number, number, number, number, number][] = [
      // [w, d, x, z, h] — door wall in two segments, then back and side walls
      [1.9, 0.35, -1.95, doorSide * 2.83, wallH],
      [1.9, 0.35, 1.95, doorSide * 2.83, wallH],
      [6, 0.35, 0, -doorSide * 2.83, wallH],
      [0.35, 5.3, -2.83, 0, wallH],
      [0.35, 5.3, 2.83, 0, wallH],
      // lintel above the door
      [2.2, 0.35, 0, doorSide * 2.83, 0],
    ];
    for (const [w, d, x, z, h] of boxes) {
      const height = h === 0 ? wallH * 0.35 : h;
      const mesh = new THREE.Mesh(new THREE.BoxGeometry(w, height, d), wallMat);
      mesh.position.set(x, h === 0 ? wallH - height / 2 : height / 2, z);
      mesh.castShadow = mesh.receiveShadow = true;
      group.add(mesh);
    }
    const roof = new THREE.Mesh(
      new THREE.ConeGeometry(4.6, 2.1, 4),
      new THREE.MeshLambertMaterial({ color: roofColor })
    );
    roof.rotation.y = Math.PI / 4;
    roof.position.y = wallH + 1.05;
    roof.castShadow = true;
    group.add(roof);
    // timber corner posts: framing with boxes is free medieval detail
    const timber = new THREE.MeshLambertMaterial({ color: 0x4a3626 });
    for (const px of [-2.83, 2.83]) {
      for (const pz of [-2.83, 2.83]) {
        const post = new THREE.Mesh(new THREE.BoxGeometry(0.28, wallH, 0.28), timber);
        post.position.set(px, wallH / 2, pz);
        post.castShadow = true;
        group.add(post);
      }
    }
  }

  /** Buildings appear as they're bought: scaffolds first, the real thing on
   * completion, gone when razed. */
  private syncBuildings(state: GameState): void {
    const seen = new Set<string>();
    for (const b of state.buildings) {
      seen.add(b.id);
      const complete = b.progress >= 1;
      const existing = this.buildingVisuals.get(b.id);
      if (existing && existing.complete === complete) continue;
      if (existing) this.scene.remove(existing.group);

      const plot = plotOf(b.team, b);
      const y = heightAt(plot.x, plot.z);
      const group = new THREE.Group();

      if (!complete) {
        this.buildScaffold(group);
      } else if (b.kind === 'farm') {
        const soil = new THREE.Mesh(
          new THREE.BoxGeometry(9, 0.15, 7),
          new THREE.MeshLambertMaterial({ color: 0x6b5232 })
        );
        soil.position.y = 0.08;
        group.add(soil);
        for (let r = 0; r < 3; r++) {
          const row = new THREE.Mesh(
            new THREE.BoxGeometry(8, 0.5, 0.8),
            new THREE.MeshLambertMaterial({ color: 0xa8a03c })
          );
          row.position.set(0, 0.4, -2 + r * 2);
          group.add(row);
        }
        const hut = new THREE.Mesh(
          new THREE.BoxGeometry(2.4, 2, 2.2),
          new THREE.MeshLambertMaterial({ color: COLORS.houseWall })
        );
        hut.position.set(-4.5, 1, 2.5);
        hut.castShadow = true;
        group.add(hut);
      } else if (b.kind === 'market') {
        const stall = new THREE.Mesh(
          new THREE.BoxGeometry(4, 1.1, 2.6),
          new THREE.MeshLambertMaterial({ color: 0x9a7a4d })
        );
        stall.position.y = 0.55;
        stall.castShadow = true;
        const awning = new THREE.Mesh(
          new THREE.BoxGeometry(4.6, 0.18, 3.2),
          new THREE.MeshLambertMaterial({ color: 0xb0483f })
        );
        awning.position.y = 2.2;
        awning.castShadow = true;
        const crate = new THREE.Mesh(
          new THREE.BoxGeometry(1, 1, 1),
          new THREE.MeshLambertMaterial({ color: 0x8a6f4d })
        );
        crate.position.set(2.8, 0.5, 1.4);
        crate.castShadow = true;
        group.add(stall, awning, crate);
      } else if (b.kind === 'smithy') {
        this.baileyShell(group, plot.z, 0xd8cbb2, 0x6e4a33);
        const chimney = new THREE.Mesh(
          new THREE.BoxGeometry(0.8, 5.4, 0.8),
          new THREE.MeshLambertMaterial({ color: COLORS.stoneDark })
        );
        chimney.position.set(1.9, 2.7, -1.9);
        chimney.castShadow = true;
        const anvil = new THREE.Mesh(
          new THREE.BoxGeometry(0.9, 0.5, 0.4),
          new THREE.MeshLambertMaterial({ color: 0x3a3a40 })
        );
        anvil.position.set(0, 0.85, 0);
        const stump = new THREE.Mesh(
          new THREE.CylinderGeometry(0.32, 0.38, 0.6, 7),
          new THREE.MeshLambertMaterial({ color: COLORS.trunk })
        );
        stump.position.set(0, 0.3, 0);
        group.add(chimney, anvil, stump);
      } else if (b.kind === 'range') {
        this.baileyShell(group, plot.z, 0x9a8a6a, COLORS.houseRoof);
        for (const tx of [-1.4, 1.4]) {
          const butt = new THREE.Mesh(
            new THREE.CylinderGeometry(0.7, 0.7, 0.35, 10),
            new THREE.MeshLambertMaterial({ color: 0xc8b878 })
          );
          butt.rotation.x = Math.PI / 2;
          const face = new THREE.Mesh(
            new THREE.CylinderGeometry(0.3, 0.3, 0.37, 10),
            new THREE.MeshLambertMaterial({ color: 0xb0483f })
          );
          face.rotation.x = Math.PI / 2;
          const zOff = plot.z > 0 ? -4.4 : 4.4;
          butt.position.set(tx, 1.1, zOff);
          face.position.set(tx, 1.1, zOff);
          group.add(butt, face);
        }
      } else if (b.kind === 'chapel') {
        this.baileyShell(group, plot.z, 0xa89a88, 0x565360, 4.2);
        const crossV = new THREE.Mesh(
          new THREE.BoxGeometry(0.16, 1.4, 0.16),
          new THREE.MeshLambertMaterial({ color: 0xd8c8a0 })
        );
        crossV.position.y = 7.1;
        const crossH = new THREE.Mesh(
          new THREE.BoxGeometry(0.7, 0.16, 0.16),
          new THREE.MeshLambertMaterial({ color: 0xd8c8a0 })
        );
        crossH.position.y = 7.3;
        group.add(crossV, crossH);
      } else if (b.kind === 'treasury') {
        this.baileyShell(group, plot.z, COLORS.stone, COLORS.stoneDark);
        const trim = new THREE.Mesh(
          new THREE.BoxGeometry(6.2, 0.3, 5.5),
          new THREE.MeshLambertMaterial({ color: 0xd8b545 })
        );
        trim.position.y = 3.35;
        group.add(trim);
      } else if (b.kind === 'housing') {
        this.baileyShell(group, plot.z, COLORS.houseWall, COLORS.houseRoof, 4.6);
      }

      group.position.set(plot.x, y, plot.z);
      this.scene.add(group);
      this.buildingVisuals.set(b.id, { group, complete });
    }
    // razed buildings (and fresh games) clear their visuals
    for (const [id, vis] of this.buildingVisuals) {
      if (!seen.has(id)) {
        this.scene.remove(vis.group);
        this.buildingVisuals.delete(id);
      }
    }
  }

  private syncArrows(state: GameState): void {
    while (this.arrowPool.length < state.projectiles.length) {
      const arrow = new THREE.Mesh(
        new THREE.BoxGeometry(0.05, 0.05, 0.75),
        new THREE.MeshLambertMaterial({ color: COLORS.arrow })
      );
      this.scene.add(arrow);
      this.arrowPool.push(arrow);
    }
    for (let i = 0; i < this.arrowPool.length; i++) {
      const mesh = this.arrowPool[i];
      const p = state.projectiles[i];
      if (!p) {
        mesh.visible = false;
        continue;
      }
      mesh.visible = true;
      mesh.position.set(p.x, heightAt(p.x, p.z) + p.y, p.z);
      mesh.rotation.set(0, Math.atan2(p.vx, p.vz), 0);
      mesh.rotateX(-Math.atan2(p.vy, Math.hypot(p.vx, p.vz)));
    }
  }

  private buildCharacterVisual(c: CharacterState): CharacterVisual {
    const palette =
      c.kind === 'soldier'
        ? (`soldier-${c.team}` as const)
        : c.team === 'enemy' && (c.kind === 'agent' || c.kind === 'king')
          ? (`${c.kind}-enemy` as const)
          : c.kind;
    const rig = new CharacterRig(c.kind, palette, c.archetype);

    const disc = new THREE.Mesh(
      new THREE.CircleGeometry(0.52, 12),
      new THREE.MeshBasicMaterial({
        color: c.team === 'friendly' ? COLORS.bannerFriendly : COLORS.bannerEnemy,
        transparent: true,
        opacity: 0.5,
      })
    );
    disc.rotation.x = -Math.PI / 2;
    disc.position.y = 0.07;
    rig.group.add(disc);

    let healthBar: THREE.Group | null = null;
    let healthFill: THREE.Mesh | null = null;
    if (c.brain !== 'player') {
      healthBar = new THREE.Group();
      healthBar.position.y = 2.35;
      const back = new THREE.Mesh(
        new THREE.PlaneGeometry(1.0, 0.12),
        new THREE.MeshBasicMaterial({ color: 0x24140f, side: THREE.DoubleSide })
      );
      healthFill = new THREE.Mesh(
        new THREE.PlaneGeometry(1.0, 0.12),
        new THREE.MeshBasicMaterial({ color: 0xc23b3b, side: THREE.DoubleSide })
      );
      healthFill.position.z = 0.005;
      healthBar.add(back, healthFill);
      rig.group.add(healthBar);
    }

    this.scene.add(rig.group);
    return { rig, healthBar, healthFill, disc, farLod: false };
  }

  /** Draw interpolated state: positions lerp between the previous tick's
   * snapshot and the current (mutated-in-place) state. */
  render(prevPos: Map<CharacterId, PosSnapshot>, curr: GameState, alpha: number): void {
    const now = performance.now();
    const dt = Math.min((now - this.lastRenderTime) / 1000, 0.1);
    this.lastRenderTime = now;
    this.frame++;

    this.syncBuildings(curr);
    this.syncArrows(curr);

    const target = curr.characters[curr.controlled];
    const tSnap = prevPos.get(curr.controlled);
    const tx = tSnap ? tSnap.x + (target.x - tSnap.x) * alpha : target.x;
    const tz = tSnap ? tSnap.z + (target.z - tSnap.z) * alpha : target.z;
    const tElev = tSnap ? tSnap.elev + (target.elev - tSnap.elev) * alpha : target.elev;
    const ty = heightAt(tx, tz) + tElev;

    // indoors: the roof stays on — the camera tightens, torches light the
    // rooms, and the outdoor fog is suspended. Photo mode judges by the
    // fixed camera instead of the controlled character.
    const refX = this.fixedCam ? this.fixedCam.x : tx;
    const refZ = this.fixedCam ? this.fixedCam.z : tz;
    const refLow = this.fixedCam ? this.fixedCam.y < 8.5 : tElev < 8;
    let indoors = false;
    for (const team of ['friendly', 'enemy'] as Team[]) {
      const kb = keepBounds(team);
      const inside =
        refLow && refX > kb.minX && refX < kb.maxX && refZ > kb.minZ && refZ < kb.maxZ;
      if (inside) indoors = true;
      const near =
        Math.abs(refX - (kb.minX + kb.maxX) / 2) < 45 &&
        Math.abs(refZ - (kb.minZ + kb.maxZ) / 2) < 55;
      for (const light of this.torchLights.get(team) ?? []) light.visible = near;
    }
    const fog = this.scene.fog as THREE.Fog;
    fog.near = indoors ? 500 : 130;
    fog.far = indoors ? 1400 : 560;

    // squad leaders wear pennants
    const leaderIds = new Set<string>();
    for (const squad of Object.values(curr.squads)) {
      if (squad.team !== 'friendly') continue;
      const leader = squadLeader(curr, squad.id);
      if (leader) leaderIds.add(leader.id);
    }

    let index = 0;
    for (const id of Object.keys(curr.characters)) {
      index++;
      const cc = curr.characters[id];
      const pc = prevPos.get(id);
      let vis = this.characters.get(id);
      if (!vis) {
        vis = this.buildCharacterVisual(cc);
        this.characters.set(id, vis);
      }

      // the fallen linger on the field — attrition is the clearest "who is
      // winning here" signal a battlefield has
      const sinceHit = curr.tick - cc.lastHitTick;
      const visible = cc.alive || sinceHit < 18 * TICK_RATE;
      vis.rig.group.visible = visible;
      vis.disc.visible = cc.alive;
      if (!visible) continue;

      const x = pc ? pc.x + (cc.x - pc.x) * alpha : cc.x;
      const z = pc ? pc.z + (cc.z - pc.z) * alpha : cc.z;
      const elev = pc ? pc.elev + (cc.elev - pc.elev) * alpha : cc.elev;
      const heading = pc ? lerpAngle(pc.heading, cc.heading, alpha) : cc.heading;

      const dist = Math.hypot(x - tx, z - tz);
      if (vis.farLod && dist < LOD_FAR_EXIT) vis.farLod = false;
      else if (!vis.farLod && dist > LOD_FAR_ENTER) vis.farLod = true;
      vis.rig.setShadows(!vis.farLod);
      const posePass = !vis.farLod || (this.frame + index) % 4 === 0;
      vis.rig.update(cc, x, heightAt(x, z) + elev, z, heading, curr.tick, dt, posePass);

      vis.rig.setFlash(sinceHit >= 0 && sinceHit < 6);
      if (cc.kind === 'soldier') vis.rig.setPennant(leaderIds.has(id));

      if (vis.healthBar && vis.healthFill) {
        vis.healthBar.visible = cc.alive && cc.health < cc.maxHealth && !vis.farLod;
        if (vis.healthBar.visible) {
          const pct = Math.max(0, cc.health / cc.maxHealth);
          vis.healthFill.scale.x = Math.max(0.001, pct);
          vis.healthFill.position.x = -(1 - pct) / 2;
          vis.healthBar.rotation.y = this.cameraYaw + Math.PI - vis.rig.group.rotation.y;
        }
      }
    }

    this.positionCamera(tx, ty, tz, indoors);
    this.renderer.render(this.scene, this.camera);
  }

  /** Photo mode: a fixed camera pose for screenshots (dev URL param). */
  fixedCam: { x: number; y: number; z: number; yaw: number; pitch: number } | null = null;

  private positionCamera(tx: number, ty: number, tz: number, indoors: boolean): void {
    if (this.fixedCam) {
      const f = this.fixedCam;
      this.camera.position.set(f.x, f.y, f.z);
      this.camera.rotation.set(0, 0, 0);
      this.camera.rotateY(f.yaw);
      this.camera.rotateX(f.pitch);
      return;
    }
    const pitch = this.cameraPitch;
    const yaw = this.cameraYaw;
    const head = new THREE.Vector3(tx, ty + 1.6, tz);
    const dir = new THREE.Vector3(
      -Math.sin(yaw) * Math.cos(pitch),
      Math.sin(pitch),
      -Math.cos(yaw) * Math.cos(pitch)
    );

    // march the boom outward and stop before it pokes through a wall or roof
    const maxD = indoors ? 3.4 : 6.5;
    let d = maxD;
    const boxes = obstacleList();
    const p = new THREE.Vector3();
    outer: for (let step = 1; step <= 13; step++) {
      const t = (step / 13) * maxD;
      p.copy(head).addScaledVector(dir, t);
      for (const w of boxes) {
        if (
          p.x > w.minX - 0.3 &&
          p.x < w.maxX + 0.3 &&
          p.z > w.minZ - 0.3 &&
          p.z < w.maxZ + 0.3 &&
          p.y < w.height + 0.2 &&
          p.y > (w.minY ?? 0) - 0.2
        ) {
          d = Math.max(1.4, t - 0.7);
          break outer;
        }
      }
    }

    const eye = head.clone().addScaledVector(dir, d);
    const floor = heightAt(eye.x, eye.z) + 0.6;
    if (eye.y < floor) eye.y = floor;
    // stay under the ceiling when indoors
    if (indoors && eye.y > 8.0) eye.y = 8.0;
    this.camera.position.copy(eye);
    this.camera.lookAt(tx, ty + 1.8, tz);
  }
}

function lerpAngle(a: number, b: number, t: number): number {
  let diff = b - a;
  while (diff > Math.PI) diff -= Math.PI * 2;
  while (diff < -Math.PI) diff += Math.PI * 2;
  return a + diff * t;
}
